import { z } from 'zod';
import { paginationInput, sortInput, idInput } from './common';
import { extrairCodigo, separarCodigoUnidade } from '../lib/codigo-produto';

/**
 * Código informado à mão por um ADMIN. Passa pelo mesmo normalizador que o
 * leitor do PDV usa, para que um código impossível de escanear não seja aceito
 * no cadastro.
 */
export const codigoProdutoInput = z
  .string()
  .trim()
  .min(1, 'Código não pode ser vazio')
  .refine((valor) => extrairCodigo(valor) !== null, {
    message: 'Código inválido. Use letras, números, ponto, hífen ou underscore (até 16 caracteres)',
  })
  // `LOTE-2` seria lido como peça 2 do produto `LOTE`. Os códigos gerados
  // (`00001`) nunca têm essa forma, mas um código informado à mão poderia.
  .refine((valor) => separarCodigoUnidade(valor) === null, {
    message: 'Código não pode terminar em hífen seguido de número — esse formato é das peças',
  });

export const categoriaInput = z
  .string()
  .trim()
  .max(80, 'Categoria deve ter no máximo 80 caracteres');

export const movimentacaoTipoEnum = z.enum(['ENTRADA', 'SAIDA']);

export const listProdutosInput = paginationInput.merge(sortInput).extend({
  search: z.string().optional(),
  ativo: z.boolean().optional(),
  estoqueBaixo: z.boolean().optional(),
  categoria: z.string().optional(),
});

export const createProdutoInput = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  /** Ausente significa "gerar pela sequence"; presente sobrescreve (R3). */
  codigo: codigoProdutoInput.optional(),
  categoria: categoriaInput.optional(),
  descricao: z.string().optional(),
  preco: z.string(),
  quantidadeEstoque: z.number().int().nonnegative().default(0),
});

export const updateProdutoInput = idInput.extend({
  nome: z.string().min(2).optional(),
  codigo: codigoProdutoInput.optional(),
  categoria: categoriaInput.optional(),
  descricao: z.string().optional(),
  preco: z.string().optional(),
  quantidadeEstoque: z.number().int().nonnegative().optional(),
  ativo: z.boolean().optional(),
});

export const ajustarEstoqueInput = z.object({
  produtoId: z.string().uuid(),
  tipo: movimentacaoTipoEnum,
  quantidade: z.number().int().positive('Quantidade deve ser positiva'),
  motivo: z.string().optional(),
});

/** Aceita o payload do QR (`LEO:00001`) ou o código digitado à mão. */
export const getProdutoByCodigoInput = z.object({
  codigo: z.string().trim().min(1, 'Informe o código do produto'),
  /**
   * Peças já no carrinho. Só importa quando o código lido é de produto, e não
   * de peça: sem isso, tocar duas vezes no mesmo produto devolveria a mesma
   * peça as duas vezes.
   */
  excluirUnidades: z.array(z.string().uuid()).optional(),
});

/**
 * O que impede uma peça de ser apagada de verdade.
 *
 * A tela consulta isso antes de abrir a confirmação, para dizer ao usuário o
 * que vai acontecer em vez de perguntar "tem certeza?" no vazio.
 */
export const exclusaoProdutoOutput = z.object({
  podeApagar: z.boolean(),
  vendas: z.number(),
  movimentacoes: z.number(),
});

export const resultadoExclusaoOutput = z.object({
  /** `false` significa que a peça foi desativada em vez de apagada. */
  apagado: z.boolean(),
  message: z.string(),
});

/**
 * Categorização em lote.
 *
 * `categoria` vazia limpa o campo — é como se tira uma categoria errada de um
 * punhado de produtos sem abrir um por um.
 */
export const atualizarCategoriaLoteInput = z.object({
  produtoIds: z.array(z.string().uuid()).min(1, 'Selecione ao menos um produto'),
  categoria: categoriaInput,
});

export const produtoOutput = z.object({
  id: z.string(),
  nome: z.string(),
  codigo: z.string(),
  categoria: z.string().nullable(),
  descricao: z.string().nullable(),
  preco: z.string(),
  quantidadeEstoque: z.number(),
  ativo: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const movimentacaoOutput = z.object({
  id: z.string(),
  produtoId: z.string(),
  tipo: movimentacaoTipoEnum,
  quantidade: z.number(),
  motivo: z.string().nullable(),
  userId: z.string(),
  createdAt: z.date(),
});

export type ListProdutosInput = z.infer<typeof listProdutosInput>;
export type GetProdutoByCodigoInput = z.infer<typeof getProdutoByCodigoInput>;
export type AtualizarCategoriaLoteInput = z.infer<typeof atualizarCategoriaLoteInput>;
export type ExclusaoProdutoOutput = z.infer<typeof exclusaoProdutoOutput>;
export type ResultadoExclusaoOutput = z.infer<typeof resultadoExclusaoOutput>;
export type CreateProdutoInput = z.infer<typeof createProdutoInput>;
export type UpdateProdutoInput = z.infer<typeof updateProdutoInput>;
export type AjustarEstoqueInput = z.infer<typeof ajustarEstoqueInput>;
export type ProdutoOutput = z.infer<typeof produtoOutput>;
export type MovimentacaoOutput = z.infer<typeof movimentacaoOutput>;
